import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Users, Sparkles, MessageSquare, BookOpen, LayoutList, 
    ArrowLeft, Loader2, Shield, Zap, Hash, Globe,
    FileText, Library
} from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, color = '#d4af37', delay = 0 }) => (
    <div 
        className="p-4 sm:p-5 bg-[#0f172a]/80 rounded-xl border border-[#1e293b] hover:border-[#334155] transition-all duration-300 hover:scale-[1.02] group"
        style={{ animationDelay: `${delay}ms` }}
    >
        <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                <Icon className="w-5 h-5" style={{ color }} />
            </div>
            {sub && (
                <span className="text-xs text-[#64748b] bg-[#1e293b] px-2 py-1 rounded-full">{sub}</span>
            )}
        </div>
        <p className="text-2xl sm:text-3xl font-bold text-[#f8fafc] mb-1 tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        <p className="text-xs text-[#64748b] uppercase tracking-wider">{label}</p>
    </div>
);

const SectionHeader = ({ icon: Icon, title, color = '#d4af37' }) => (
    <div className="flex items-center gap-2 mb-4 mt-8 first:mt-0">
        <Icon className="w-5 h-5" style={{ color }} />
        <h2 className="text-lg font-semibold text-[#f8fafc]">{title}</h2>
    </div>
);

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { isAuthenticated, getAuthHeaders, isLoading: authLoading } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!isAuthenticated) {
            navigate('/');
            return;
        }

        const fetchStats = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`${API}/admin/stats`, {
                    headers: getAuthHeaders()
                });
                setStats(response.data);
            } catch (err) {
                if (err.response?.status === 403) {
                    navigate('/');
                    return;
                }
                setError(err.response?.data?.detail || 'Failed to load dashboard');
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [isAuthenticated, authLoading, getAuthHeaders, navigate]);

    const endpointLabels = {
        'ai-translate': { label: 'AI Translations', icon: FileText, color: '#d4af37' },
        'ai-generate-word-meaning': { label: 'AI Word Meanings', icon: BookOpen, color: '#8b5cf6' },
        'ai-search-verses': { label: 'AI Verse Search', icon: Sparkles, color: '#3b82f6' }
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-[#02040a] flex items-center justify-center">
                <div className="text-center space-y-4">
                    <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin mx-auto" />
                    <p className="text-[#94a3b8] text-sm">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#02040a] flex items-center justify-center">
                <div className="text-center space-y-4">
                    <Shield className="w-12 h-12 text-[#ef4444] mx-auto" />
                    <p className="text-[#ef4444] text-lg font-medium">{error}</p>
                    <Button onClick={() => navigate('/')} className="bg-[#1e293b] text-[#f8fafc]">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to App
                    </Button>
                </div>
            </div>
        );
    }

    if (!stats) return null;

    return (
        <div className="min-h-screen bg-[#02040a] relative">
            <div className="bg-texture" />
            
            <div className="relative z-10">
                {/* Header */}
                <header className="border-b border-[#1e293b] bg-[#02040a]/90 backdrop-blur-md sticky top-0 z-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Button 
                                onClick={() => navigate('/')} 
                                variant="ghost" 
                                size="sm"
                                className="text-[#94a3b8] hover:text-[#f8fafc] -ml-2"
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </Button>
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#ef4444] to-[#dc2626] flex items-center justify-center">
                                <Shield className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg sm:text-xl font-bold text-[#f8fafc]">Admin Dashboard</h1>
                                <p className="text-xs text-[#64748b]">Ai Quran • Application Statistics</p>
                            </div>
                        </div>
                        <span className="text-xs text-[#64748b] hidden sm:block">
                            Last updated: {new Date().toLocaleString()}
                        </span>
                    </div>
                </header>

                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-2">

                    {/* Overview Stats */}
                    <SectionHeader icon={Zap} title="Overview" color="#d4af37" />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                        <StatCard icon={Users} label="Total Users" value={stats.users.total} color="#3b82f6" delay={0} />
                        <StatCard icon={Sparkles} label="Total AI Queries" value={stats.ai_usage.total_queries} sub="all time" color="#8b5cf6" delay={50} />
                        <StatCard icon={Hash} label="Total Tokens" value={stats.ai_usage.total_tokens} color="#f59e0b" delay={100} />
                        <StatCard icon={Sparkles} label="AI Queries (30d)" value={stats.ai_usage.recent_30_days} sub="last 30 days" color="#10b981" delay={150} />
                    </div>

                    {/* AI Usage by Type */}
                    <SectionHeader icon={Sparkles} title="AI Usage by Type" color="#8b5cf6" />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                        {Object.entries(endpointLabels).map(([key, { label, icon: EIcon, color }], idx) => {
                            const data = stats.ai_usage.by_endpoint[key] || { count: 0, tokens: 0 };
                            return (
                                <StatCard 
                                    key={key} 
                                    icon={EIcon} 
                                    label={label} 
                                    value={data.count} 
                                    sub={`${data.tokens.toLocaleString()} tokens`} 
                                    color={color} 
                                    delay={idx * 50} 
                                />
                            );
                        })}
                    </div>

                    {/* Daily Trend Chart */}
                    {stats.ai_usage.daily_trend && (
                        <>
                            <SectionHeader icon={Zap} title="Daily AI Queries (Last 30 Days)" color="#10b981" />
                            <div className="bg-[#0f172a]/80 rounded-xl border border-[#1e293b] p-4 sm:p-6">
                                {(() => {
                                    const trend = stats.ai_usage.daily_trend;
                                    const maxCount = Math.max(...trend.map(d => d.count), 1);
                                    const totalInPeriod = trend.reduce((sum, d) => sum + d.count, 0);
                                    const avgPerDay = (totalInPeriod / trend.length).toFixed(1);
                                    
                                    return (
                                        <>
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex gap-4">
                                                    <span className="text-xs text-[#64748b]">
                                                        Total: <span className="text-[#f8fafc] font-medium">{totalInPeriod}</span>
                                                    </span>
                                                    <span className="text-xs text-[#64748b]">
                                                        Avg/day: <span className="text-[#f8fafc] font-medium">{avgPerDay}</span>
                                                    </span>
                                                    <span className="text-xs text-[#64748b]">
                                                        Peak: <span className="text-[#f8fafc] font-medium">{maxCount}</span>
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-end gap-[2px] sm:gap-1 h-32 sm:h-40">
                                                {trend.map((day, idx) => {
                                                    const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                                                    const date = new Date(day.date + 'T00:00:00');
                                                    const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                                    return (
                                                        <div 
                                                            key={idx} 
                                                            className="flex-1 flex flex-col items-center justify-end h-full group relative"
                                                        >
                                                            {/* Tooltip */}
                                                            <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-[#1e293b] border border-[#334155] rounded-lg px-2 py-1 text-xs text-[#f8fafc] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none shadow-lg">
                                                                <span className="font-medium">{day.count}</span>
                                                                <span className="text-[#64748b] ml-1">{label}</span>
                                                            </div>
                                                            {/* Bar */}
                                                            <div 
                                                                className="w-full rounded-t transition-all duration-300 hover:opacity-80 cursor-pointer"
                                                                style={{ 
                                                                    height: `${Math.max(height, day.count > 0 ? 3 : 0)}%`,
                                                                    background: day.count > 0 
                                                                        ? `linear-gradient(to top, #10b981, #34d399)`
                                                                        : '#1e293b',
                                                                    minHeight: day.count > 0 ? '3px' : '1px',
                                                                    opacity: day.count > 0 ? 1 : 0.3
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {/* X-axis labels - show every 5th day */}
                                            <div className="flex gap-[2px] sm:gap-1 mt-2">
                                                {trend.map((day, idx) => {
                                                    const date = new Date(day.date + 'T00:00:00');
                                                    const showLabel = idx % 5 === 0 || idx === trend.length - 1;
                                                    return (
                                                        <div key={idx} className="flex-1 text-center">
                                                            {showLabel && (
                                                                <span className="text-[9px] sm:text-[10px] text-[#475569]">
                                                                    {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </>
                    )}

                    {/* Content Stats */}
                    <SectionHeader icon={MessageSquare} title="Content Statistics" color="#10b981" />
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                        <StatCard icon={MessageSquare} label="Custom Meanings" value={stats.content.custom_meanings} color="#d4af37" delay={0} />
                        <StatCard icon={FileText} label="Custom Translations" value={stats.content.custom_translations} color="#10b981" delay={50} />
                        <StatCard icon={LayoutList} label="Total Topics" value={stats.content.topics} color="#3b82f6" delay={100} />
                        <StatCard icon={Globe} label="Public Topics" value={stats.content.public_topics} color="#8b5cf6" delay={150} />
                        <StatCard icon={BookOpen} label="Topic Verses" value={stats.content.topic_verses} color="#f59e0b" delay={200} />
                    </div>

                    {/* Users Table */}
                    <SectionHeader icon={Users} title="Registered Users" color="#3b82f6" />
                    <div className="bg-[#0f172a]/80 rounded-xl border border-[#1e293b] overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-[#1e293b]">
                                        <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium">#</th>
                                        <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium">Name</th>
                                        <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium">Email</th>
                                        <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium hidden sm:table-cell">Last Login</th>
                                        <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium hidden md:table-cell">Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.users.list.map((user, idx) => (
                                        <tr key={idx} className="border-b border-[#1e293b]/50 hover:bg-[#1e293b]/30 transition-colors">
                                            <td className="px-4 py-3 text-[#64748b]">{idx + 1}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {user.picture && (
                                                        <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                                                    )}
                                                    <span className="text-[#f8fafc] font-medium">{user.name || 'Unknown'}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-[#94a3b8]">{user.email}</td>
                                            <td className="px-4 py-3 text-[#64748b] hidden sm:table-cell">
                                                {user.last_login ? new Date(user.last_login).toLocaleDateString() : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-[#64748b] hidden md:table-cell">
                                                {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Top AI Users */}
                    {stats.ai_usage.top_users.length > 0 && (
                        <>
                            <SectionHeader icon={Sparkles} title="Top AI Users" color="#f59e0b" />
                            <div className="bg-[#0f172a]/80 rounded-xl border border-[#1e293b] overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-[#1e293b]">
                                                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium">#</th>
                                                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium">Email</th>
                                                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium">Queries</th>
                                                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium">Tokens</th>
                                                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium">Word Meanings</th>
                                                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium">Verse Meanings</th>
                                                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-[#64748b] font-medium">Last Search</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stats.ai_usage.top_users.map((user, idx) => (
                                                <tr key={idx} className="border-b border-[#1e293b]/50 hover:bg-[#1e293b]/30 transition-colors">
                                                    <td className="px-4 py-3 text-[#64748b]">{idx + 1}</td>
                                                    <td className="px-4 py-3 text-[#f8fafc]">{user.email}</td>
                                                    <td className="px-4 py-3 text-[#d4af37] text-right font-medium tabular-nums">{user.queries.toLocaleString()}</td>
                                                    <td className="px-4 py-3 text-[#94a3b8] text-right tabular-nums">{user.tokens.toLocaleString()}</td>
                                                    <td className="px-4 py-3 text-[#10b981] text-right tabular-nums">{user.custom_words?.toLocaleString() || 0}</td>
                                                    <td className="px-4 py-3 text-[#3b82f6] text-right tabular-nums">{user.custom_verses?.toLocaleString() || 0}</td>
                                                    <td className="px-4 py-3 text-[#64748b] text-right">
                                                        {user.last_search ? new Date(user.last_search).toLocaleDateString() : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}

                </main>

                {/* Footer */}
                <footer className="border-t border-[#1e293b] py-6 px-4 mt-8">
                    <div className="max-w-7xl mx-auto text-center">
                        <p className="text-[#475569] text-xs">
                            Admin Dashboard • Ai Quran • Data sourced from MongoDB
                        </p>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default AdminDashboard;
